/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable */

import EditableLabel from '@//components/EditableLabel';
import {
  useEditProject,
  useGetProjectList,
  useGetProjectsByEvents,
  useUpdateProject,
} from '@/adapters/projects';
import { useResetSegment, useDeleteProject } from '@/adapters/workbentch';
import emptyPic from '@/assets/images/projects/empty.svg';
import DefaultButton from '@/components/common/DefaultButton';
import Loading from '@/components/Loading';
import { PROJECT_URL, PROJECT_VIDEO_URL, WORKBENCH_URL } from '@/routePaths';
import {
  Box,
  Typography,
  Snackbar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import UploadButton from './Upload';
import { UserContext } from '@/contexts/UserContext';
import { useListEvents } from '@/adapters/events-v2';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import { styled } from '@mui/material/styles';
import classNames from 'classnames';
import moment from 'moment';
import React, { FC, Fragment, useEffect, useRef } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import NoProjects from './NoProjects';
import styles from './Projects.module.scss';
import ProjectListItem from '@/types/project-list-item';
import { convertS3PathToHttpUrl } from '@/utils/s3Utils';
const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  border: `1px solid var(--silver)`,
  borderRadius: '8px',
  marginBottom: '15px',
  '&:before': {
    display: 'none',
  },
}));
const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary {...props} />
))(() => ({
  backgroundColor: 'var(--gallery)',
  borderBottomColor: 'transparent',
  borderRadius: '8px',
  flexDirection: 'row-reverse',
  padding: 0,
  '&.Mui-expanded': {
    borderRadius: '8px 8px 0 0',
  },
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    margin: 0,
    padding: '10px',
  },
}));
const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  padding: 0,
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));
{
  /* eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call */
}

const EMPTY_ARRAY: never[] = [];
const Projects: FC = () => {
  const { userState } = React.useContext(UserContext);
  const navigate = useNavigate();

  // Get all projects
  const { data: allProjects = [], isLoading: isLoadingAllProjects } = useGetProjectList();

  // Get projects for managed events (if user is a manager)
  const eventIds = userState?.eventsManaged || EMPTY_ARRAY;
  const { data: managedProjects = [], isLoading: isLoadingManagedProjects } =
    useGetProjectsByEvents(eventIds);

  // Combine projects: all projects + managed projects (remove duplicates)
  const projects = React.useMemo(() => {
    const projectMap = new Map<string, ProjectListItem>();

    // Add all projects
    allProjects.forEach((project) => {
      projectMap.set(project.id, project);
    });

    // Add managed projects (will overwrite duplicates)
    managedProjects.forEach((project) => {
      projectMap.set(project.id, project);
    });

    return Array.from(projectMap.values());
  }, [allProjects, managedProjects]);

  const isLoading = isLoadingAllProjects || isLoadingManagedProjects;
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const [highlightedVideo, setHighlightedVideo] = React.useState<string | null>(null);
  const [highlight, setHighlight] = React.useState<boolean>(false);
  const [showCopiedMessage, setShowCopiedMessage] = React.useState<boolean>(false);
  const [eventMenuAnchor, setEventMenuAnchor] = React.useState<{
    projectId: string;
    element: HTMLElement;
  } | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = React.useState<{
    open: boolean;
    projectId: string | null;
    projectName: string | null;
  }>({ open: false, projectId: null, projectName: null });

  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };
  const [currentProjectId, setCurrentProjectId] = React.useState<string>('');
  const { mutateAsync: editProject } = useEditProject(currentProjectId);
  const { mutateAsync: updateProject } = useUpdateProject();
  const editableLabelRef = useRef<HTMLDivElement>(null);
  const { mutateAsync: deleteProject } = useDeleteProject();
  const { mutateAsync: resetSegment } = useResetSegment();
  const { data: events = [], isLoading: loadingEvents } = useListEvents(userState?.orgId);
  const effectiveRole = userState?.dbRole ?? userState?.role;
  const showEventColumn = (effectiveRole?.toLowerCase() ?? '') !== 'pro';

  const getSegmentDownloadUrl = (edit: { pathKey?: string; fullPath?: string }): string | null => {
    const path = edit.fullPath ?? edit.pathKey;
    if (!path) return null;
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    if (path.startsWith('s3://')) {
      try {
        return convertS3PathToHttpUrl(path);
      } catch {
        return null;
      }
    }
    return null;
  };

  const downloadSegment = (edit: { pathKey?: string; fullPath?: string; name?: string }): void => {
    const url = getSegmentDownloadUrl(edit);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `${edit.name || 'segment'}.mp4`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const viewVideo = (project: ProjectListItem, videoId: string): void => {
    // Store the project and video ids in the local storage
    localStorage.setItem('selectedProjectId', project.id);
    localStorage.setItem('selectedVideoId', videoId);

    if (project.projectType === 'UPLOADED') {
      if (videoId === 'original') {
        navigate(generatePath(PROJECT_URL, { projectId: project.id }));
      } else {
        navigate(generatePath(PROJECT_VIDEO_URL, { projectId: project.id, videoId: videoId }));
      }
    } else {
      if (videoId === 'original') {
        navigate(generatePath(WORKBENCH_URL, { projectId: project.id }));
      } else {
        navigate(generatePath(PROJECT_VIDEO_URL, { projectId: project.id, videoId: videoId }));
      }
    }
  };
  const handleEditClick = (projectId: number): void => {
    setCurrentProjectId(projectId.toString());
  };

  /**
   * Handles opening the event dropdown menu for a project
   * Reason: Allows users to assign projects to events
   */
  const handleEventMenuOpen = (projectId: string, event: React.MouseEvent<HTMLElement>): void => {
    setEventMenuAnchor({ projectId, element: event.currentTarget });
  };

  /**
   * Handles closing the event dropdown menu
   * Reason: Cleans up state when menu is closed
   */
  const handleEventMenuClose = (): void => {
    setEventMenuAnchor(null);
  };

  /**
   * Opens the delete confirmation dialog
   * Reason: Prevents accidental project deletion by requiring user confirmation
   */
  const handleDeleteClick = (projectId: string, projectName: string): void => {
    setDeleteConfirmDialog({ open: true, projectId, projectName });
  };

  /**
   * Closes the delete confirmation dialog without deleting
   * Reason: Allows user to cancel the delete operation
   */
  const handleDeleteCancel = (): void => {
    setDeleteConfirmDialog({ open: false, projectId: null, projectName: null });
  };

  /**
   * Confirms and executes the project deletion
   * Reason: Deletes the project after user confirmation
   */
  const handleDeleteConfirm = async (): Promise<void> => {
    if (deleteConfirmDialog.projectId) {
      await deleteProject(deleteConfirmDialog.projectId);
      setDeleteConfirmDialog({ open: false, projectId: null, projectName: null });
    }
  };

  /**
   * Handles assigning a project to an event
   * Reason: Updates the project's eventId to link it to an event
   */
  const handleAssignEvent = async (projectId: string, eventId: string | null): Promise<void> => {
    try {
      await updateProject({ projectId, data: { eventId: eventId || undefined } });
      handleEventMenuClose();
    } catch (err) {
      console.error('Failed to assign project to event:', err);
      alert('Failed to assign project to event. Please try again.');
    }
  };

  /**
   * Copies the public share URL to clipboard using numeric user ID from UserContext
   * Reason: Allow users to easily share their project library with others
   */
  const handleCopyShareLink = async (): Promise<void> => {
    if (userState?.id) {
      const shareUrl = `${window.location.origin}/public/${userState.id}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        setShowCopiedMessage(true);
        setTimeout(() => setShowCopiedMessage(false), 3000);
      } catch (err) {
        console.error('Failed to copy share link:', err);
        alert(`Share this URL: ${shareUrl}`);
      }
    } else {
      alert('Unable to generate share link. Please try again later.');
    }
  };

  // Track whether initial expansion has been done to avoid resetting on every projects change
  const hasInitialExpanded = useRef(false);

  // const isProjectEditable = (projectId: number): boolean => {
  //   return editableProjects.length === 1 && editableProjects.includes(projectId);
  // };
  useEffect(() => {
    if ((projects as any[]).length > 0 && !hasInitialExpanded.current) {
      hasInitialExpanded.current = true;

      // Retrieve the project and video ids from the local storage
      const storedProjectId = localStorage.getItem('selectedProjectId');
      const storedVideoId = localStorage.getItem('selectedVideoId');

      // Expand desired accordion
      setExpanded(storedProjectId && storedVideoId ? storedProjectId : projects[0].id);
      setHighlightedVideo(storedVideoId);
      // Trigger highlight
      setHighlight(true);

      // Remove after 1 sec
      const timeoutId = setTimeout(() => {
        setHighlight(false);
      }, 2000);

      // Cleanup the timeout to avoid memory leaks
      return () => clearTimeout(timeoutId);
    }
  }, [projects]);

  return (
    <>
      <div className={styles.navbar}>
        <h1 className={styles.projectsHeadTitle}>Projects Library</h1>
        {/* Speaker-role (user) accounts only see their assigned segments —
            hide the editor-only actions (share library, upload video). Gate
            on "role loaded AND not user" so nothing flashes in during the
            login/role-loading window. */}
        {!!effectiveRole && effectiveRole !== 'user' && (
          <Box sx={{ display: 'flex', gap: '10px' }}>
            <DefaultButton onClick={handleCopyShareLink}>Share Library</DefaultButton>
            <UploadButton />
          </Box>
        )}
      </div>
      <Snackbar
        open={showCopiedMessage}
        autoHideDuration={3000}
        onClose={() => setShowCopiedMessage(false)}
        message="Share link copied to clipboard!"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      {isLoading ? (
        <Loading />
      ) : (projects as any[]).length === 0 ? (
        <NoProjects
          media={<img src={emptyPic} alt="no projects" />}
          text={
            <>
              <p>You don't have projects yet.</p>

              <div className={styles.useDescription}>
                <p>
                  Copy your Stream Key in your favorite platform to start streaming to Edit on the
                  Spot
                </p>
                <p>For more information consult Tutorials</p>
              </div>
            </>
          }
          // linkText={'Connect to Zoom App'}
          // link={connectToZoomLink}
        />
      ) : (
        <>
          {
            <>
              <div className={styles.table}>
                <div className={styles.tableTitle}>
                  <div className={styles.tableItem}>Project Name</div>
                  {showEventColumn && <div className={styles.tableItem}>Event Name</div>}
                  <div className={styles.tableItem}>Date</div>
                  <div className={classNames(styles.tableItem, styles.hiddenSm)}>Time</div>
                  <div className={styles.tableItem}>Action</div>
                </div>
                {projects.map((project) => (
                  <Accordion
                    key={project.id}
                    expanded={expanded === project.id}
                    onChange={handleChange(project.id)}
                  >
                    <AccordionSummary>
                      <Typography
                        sx={{ flex: '5 1', padding: 0 }}
                        className={styles.tableItem + ' ' + styles.specialColumn}
                        onClick={() => handleEditClick(Number(project.id))}
                        style={{ cursor: 'pointer' }}
                        ref={editableLabelRef}
                        data-project-id={project.id}
                      >
                        {currentProjectId === project.id.toString() ? (
                          <EditableLabel
                            key={project.id}
                            text={project.name}
                            onSave={(text) => editProject({ name: text })}
                            testId="project-name"
                            icon="off"
                          />
                        ) : (
                          project.name
                        )}
                      </Typography>
                      {showEventColumn && (
                        <Typography
                          sx={{ flex: '1 1', padding: 0 }}
                          className={styles.specialColumn}
                        >
                          {project.eventName || '-'}
                        </Typography>
                      )}
                      <Typography sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                        {moment(project.created).format('MMMM D, YYYY')}
                      </Typography>
                      <Typography sx={{ flex: '1 1', padding: 0 }} className={styles.specialColumn}>
                        {moment(project.created).format('hh:mm A')}
                      </Typography>
                      <Typography sx={{ flex: '1 1', padding: 0 }}></Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      {!project.original && (project.edits as any[]).length === 0 && (
                        <Box sx={{ display: 'flex', padding: '10px' }}>
                          <Typography sx={{ flex: '5 1', padding: 0 }}>No video found.</Typography>
                        </Box>
                      )}
                      {project.original && (
                        <Box sx={{ display: 'flex', padding: '10px' }}>
                          <Typography
                            sx={{ flex: '5 1', padding: 0, lineHeight: '32px' }}
                            className={styles.tableItem + ' ' + styles.specialColumn}
                          >
                            Original
                          </Typography>
                          {showEventColumn && (
                            <Typography
                              sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                              className={styles.specialColumn}
                            >
                              {project.eventName || '-'}
                            </Typography>
                          )}
                          <Typography
                            sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                            className={styles.specialColumn}
                          >
                            {moment(project.created).format('MMMM D, YYYY')}
                          </Typography>
                          <Typography
                            sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                            className={styles.specialColumn}
                          >
                            {moment(project.created).format('hh:mm A')}
                          </Typography>
                          <Box
                            sx={{ flex: '1 1', padding: 0, display: 'flex', gap: '5px' }}
                            className={styles.tableBtn}
                          >
                            <DefaultButton
                              size="small"
                              sx={{ paddingLeft: 1, paddingRight: 1, maxHeight: '32px' }}
                              onClick={() => viewVideo(project, 'original')}
                            >
                              View
                            </DefaultButton>
                            {!!effectiveRole && effectiveRole !== 'user' && (
                              <DefaultButton
                                size="small"
                                sx={{ paddingLeft: 1, paddingRight: 1, maxHeight: '32px' }}
                                onClick={(e) => handleEventMenuOpen(project.id, e)}
                              >
                                Event
                              </DefaultButton>
                            )}
                            <DefaultButton
                              size="small"
                              sx={{ paddingLeft: 1, paddingRight: 1, maxHeight: '32px' }}
                              onClick={() => handleDeleteClick(project.id, project.name)}
                            >
                              Delete
                            </DefaultButton>
                          </Box>
                        </Box>
                      )}
                      {project.edits.map((edit) => (
                        <Fragment key={edit.id}>
                          {(edit.status === 'Processed' || edit.status === 'Processing') && (
                            <Box
                              sx={{
                                display: 'flex',
                                padding: '10px',
                                ...(edit.id === highlightedVideo && highlight
                                  ? {
                                      animation: 'highlightAnimation 2s ease-out',
                                    }
                                  : {}),
                                '@keyframes highlightAnimation': {
                                  '0%': { border: '1px solid #191c43' },
                                  '100%': { border: '1px solid transparent' },
                                },
                              }}
                            >
                              {/* <Typography sx={{ flex: '5 1', padding: 0, lineHeight: '32px' }}>
                                {edit.name}
                              </Typography> */}
                              <Typography
                                sx={{ flex: '5 1', padding: 0, lineHeight: '32px' }}
                                className={styles.tableItem + ' ' + styles.specialColumn}
                              >
                                {edit.name}
                              </Typography>
                              {showEventColumn && (
                                <Typography
                                  sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                                  className={styles.specialColumn}
                                >
                                  {project.eventName || '-'}
                                </Typography>
                              )}
                              <Typography
                                sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                                className={styles.specialColumn}
                              >
                                {moment(project.created).format('MMMM D, YYYY')}
                              </Typography>
                              <Typography
                                sx={{ flex: '1 1', padding: 0, lineHeight: '32px' }}
                                className={styles.specialColumn}
                              >
                                {moment(project.created).format('hh:mm A')}
                              </Typography>
                              <Box
                                sx={{ flex: '1 1', padding: 0, display: 'flex', gap: '5px' }}
                                className={styles.tableBtn}
                              >
                                {userState?.role === 'user' ? (
                                  // Speakers can only download their segment from the
                                  // projects list — they don't have edit / reset access.
                                  <DefaultButton
                                    size="small"
                                    sx={{
                                      paddingLeft: 1,
                                      paddingRight: 1,
                                      maxHeight: '32px',
                                    }}
                                    disabled={!getSegmentDownloadUrl(edit)}
                                    onClick={() => downloadSegment(edit)}
                                  >
                                    Download
                                  </DefaultButton>
                                ) : (
                                  <>
                                    <DefaultButton
                                      size="small"
                                      sx={{
                                        paddingLeft: 1,
                                        paddingRight: 1,
                                        maxHeight: '32px',
                                      }}
                                      onClick={() => {
                                        localStorage.setItem('selectedSegmentName', edit.name);
                                        viewVideo(project, edit.id);
                                      }}
                                    >
                                      Edit
                                    </DefaultButton>
                                    <DefaultButton
                                      size="small"
                                      sx={{
                                        paddingLeft: 1,
                                        paddingRight: 1,
                                        maxHeight: '32px',
                                      }}
                                      disabled={!getSegmentDownloadUrl(edit)}
                                      onClick={() => downloadSegment(edit)}
                                    >
                                      Download
                                    </DefaultButton>
                                    <DefaultButton
                                      size="small"
                                      sx={{
                                        paddingLeft: 1,
                                        paddingRight: 1,
                                        maxHeight: '32px',
                                      }}
                                      onClick={() => resetSegment(edit.id)}
                                    >
                                      Delete
                                    </DefaultButton>
                                  </>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Fragment>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                ))}
                <div className={styles.tableTitle}>
                  <div className={styles.tableItem}>Project Name</div>
                  {showEventColumn && <div className={styles.tableItem}>Event Name</div>}
                  <div className={styles.tableItem}>Date</div>
                  <div className={classNames(styles.tableItem, styles.hiddenSm)}>Time</div>
                  <div className={styles.tableItem}>Action</div>
                </div>
              </div>
            </>
          }
        </>
      )}
      <Menu
        anchorEl={eventMenuAnchor?.element}
        open={!!eventMenuAnchor}
        onClose={handleEventMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem
          onClick={() => {
            if (eventMenuAnchor) {
              void handleAssignEvent(eventMenuAnchor.projectId, null);
            }
          }}
        >
          <em>No Event</em>
        </MenuItem>
        {loadingEvents ? (
          <MenuItem disabled>Loading events...</MenuItem>
        ) : (
          events
            .filter((event) => event.isActive)
            .map((event) => (
              <MenuItem
                key={event.id}
                onClick={() => {
                  if (eventMenuAnchor) {
                    void handleAssignEvent(eventMenuAnchor.projectId, event.id);
                  }
                }}
              >
                {event.name}
                {event.description && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="textSecondary"
                    sx={{ ml: 1 }}
                  >
                    - {event.description}
                  </Typography>
                )}
              </MenuItem>
            ))
        )}
      </Menu>
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Project</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the project &quot;{deleteConfirmDialog.projectName}
            &quot;? This action cannot be undone and will permanently remove the project and all its
            segments.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={() => void handleDeleteConfirm()}
            color="error"
            variant="contained"
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default Projects;
